import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import _ from 'lodash';
import uniqid from 'uniqid';
import Case from 'case';
import { Box, Text } from 'grommet';
import { useAppTheme } from '@Components/Context';
import { withProductAuth } from '@Components/Layout';
import {
  Seo,
  AppButton,
  Spinning,
  UnderConstruction,
  RadioButtonSlider,
  SubmissionLoader,
  InfoNotice,
} from '@Components/Control';
import { AuthNavWrapper } from '@Components/Navigation';
import {
  ProductFormSectionWrapper,
  ProductFormDropdownSelect,
  ProductFormFieldContainer,
  ProductFormCheckBoxInput,
} from '@Components/Partial/Product/NewSearch';
import {
  initiateResultsUploadConfigRequest,
  clearResultsUploadConfigRequest,
  submitProductResultsImportRequest,
  submitProductSingleResultImportRequest,
} from '@Actions';
import DynamicFormRenderer from '../Searches/NewSearch/DynamicFormRenderer';


const BannerChildren = ({
  bgColor, textColor, setBulkUpload, activeBg,
}) => (
  <Box direction="row" justify="between">
    <RadioButtonSlider
      radioOptions={[{ label: 'Bulk upload', value: 'bulk' }, { label: 'Single upload', value: 'individual' }]}
      componentBg={bgColor}
      textColor={textColor}
      activeButtonBg={activeBg}
      updateParent={setBulkUpload}
    />
  </Box>
);

BannerChildren.propTypes = {
  bgColor: PropTypes.string.isRequired,
  textColor: PropTypes.string.isRequired,
  setBulkUpload: PropTypes.func.isRequired,
  activeBg: PropTypes.string.isRequired,
};

const FormContents = ({
  small,
  loading,
  underMaintenance,
  showInfoNotice,
  requiredFormInputs = null,
  optionalFormInputs = null,
  marketplaceFormInputs = null,
  showMarketplaceFields = false,
  toggleShowMarketplaceFields,
  bulkUpload,
  categories = null,
  headerMappings = null,
  formValues,
  handleFormValues,
  minStartDate = null,
  timeZone,
  setTimeZone,
  searchTypeKey = null,
  searchTypeDisplay = null,
  platformOptionOverrides = null,
  handleSearchType,
  resetForm,
  submitEnabled,
  handleFormSubmit,
}) => {
  const { theme: { page, button, navigation }, constructionImage } = useAppTheme();

  if (underMaintenance) {
    return (
      <Box flex align="center" justify="center" background={page.background}>
        <UnderConstruction
          imageUrl={constructionImage}
          buttonHighlight={page.highlight}
          header="Under Maintenance"
          message="System currently undergoing maintenance. Please check back later."
        />
      </Box>
    );
  }

  if (loading) {
    return (
      <Box flex align="center" justify="center" background={page.background}>
        <Spinning size="large" color={page.highlight} />
      </Box>
    );
  }

  const activeHeaders = headerMappings?.find(
    ({ category }) => category === searchTypeKey,
  )?.headers || [];
  const displayCategories = categories?.map(({ display }) => display);
  const submitLabel = bulkUpload ? 'Upload results' : 'Upload result';
  const fieldsLabel = bulkUpload ? 'File Upload' : 'Required Fields';
  const camelSearchTypeKey = Case.camel(searchTypeKey);
  const [requiredHeaders, optionalHeaders] = _.partition(activeHeaders, 'required');

  const handleDownloadExampleCSV = () => {
    const columnNames = activeHeaders.map(({ name }) => name);
    const csvContent = `${columnNames.join(',')}\n`;

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = `bulk_upload_template_${searchTypeKey}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  return (
    <Box direction="column">
      {showInfoNotice && (
        <Box pad={{ top: '1rem' }}>
          <InfoNotice
            header="Publisher Premium and Publisher Premium Social"
            text="Publisher Premium is for results found on websites, while Publisher Premium Social is for results found on social media platforms. Please select the appropriate option based on where the infringing content was located."
          />
        </Box>
      )}
      <Box
        flex
        direction={small ? 'column' : 'row'}
        gap="1.5rem"
        justify={small ? 'start' : 'evenly'}
        pad={small ? '1.5rem' : { horizontal: '1.5rem', bottom: '1.5rem', top: showInfoNotice ? '0.5rem' : '1.5rem' }}
        width="100%"
      >
        <Box gap="2rem" direction="column" style={!small ? { flex: 1, minWidth: 0 } : {}}>
          <ProductFormSectionWrapper
            small={small}
            sectionBg={page.altBg}
            sectionTitle={fieldsLabel}
            titleColor={page.primaryText}
            borderColor={navigation.border}
          >
            {Array.isArray(displayCategories) && displayCategories?.length > 1 && (
              <ProductFormFieldContainer
                small={small}
                fieldTitle="Category"
                primaryText={page.primaryText}
                hintText={page.hintText}
                borderColor={navigation.border}
              >
                <ProductFormDropdownSelect
                  selectable
                  autoSelect
                  label={searchTypeDisplay}
                  handleFormValues={(searchTypeName) => handleSearchType(searchTypeName)}
                  simpleOptions={displayCategories && displayCategories}
                />
              </ProductFormFieldContainer>
            )}
            {Array.isArray(requiredFormInputs) && requiredFormInputs?.length > 0
              && requiredFormInputs.map((dt) => (
                <DynamicFormRenderer
                  autoSelectSingleRadio
                  hideIfSingleRadio
                  key={dt.key}
                  resetKey={formValues.resetKey}
                  searchTypeKey={camelSearchTypeKey}
                  fieldData={dt}
                  small={small}
                  handleFormValues={(value, key) => handleFormValues(value, key)}
                  formValues={formValues.searchFieldValues}
                  minStartDate={minStartDate}
                  timeZone={timeZone}
                  setTimeZone={setTimeZone}
                  platformOptionOverrides={platformOptionOverrides}
                />
              ))}
          </ProductFormSectionWrapper>
          {Array.isArray(marketplaceFormInputs) && marketplaceFormInputs?.length ? (
            <Box direction="row" gap="1rem" justify="start">
              <ProductFormCheckBoxInput
                small={small}
                key={uniqid()}
                bgColor="white"
                borderColor={page.inputBorder}
                checkmarkColor={page.highlight}
                focusHighlight={page.focusHighlight}
                value={showMarketplaceFields}
                handleFormValues={() => toggleShowMarketplaceFields()}
                label={`${showMarketplaceFields ? 'Hide' : 'Show'} Marketplace Fields`}
                customBoxSize="1.25rem"
                customCheckmarkSize="1.1rem"
              />
            </Box>
          ) : null}
          {showMarketplaceFields && (
            <Box gap="2rem" width="100%" direction="column">
              <ProductFormSectionWrapper
                small={small}
                sectionBg={page.altBg}
                sectionTitle="Optional Marketplace Fields"
                titleColor={page.primaryText}
                borderColor={navigation.border}
              >
                {marketplaceFormInputs.map((dt) => (
                  <DynamicFormRenderer
                    key={dt.key}
                    resetKey={formValues.resetKey}
                    searchTypeKey={camelSearchTypeKey}
                    fieldData={dt}
                    small={small}
                    handleFormValues={(value, key) => handleFormValues(value, key)}
                    formValues={formValues.searchFieldValues}
                    minStartDate={minStartDate}
                    timeZone={timeZone}
                    setTimeZone={setTimeZone}
                    platformOptionOverrides={platformOptionOverrides}
                  />
                ))}
              </ProductFormSectionWrapper>
            </Box>
          )}
          <Box direction="row" gap="1rem" justify="start" pad={{ left: '2rem' }}>
            <AppButton
              width="6.5rem"
              onClick={() => resetForm()}
              level="dynamicLarge"
              color={button.primaryBg}
              fontWeight={600}
              label="Cancel"
            />
            <AppButton
              overrideHover
              disabled={!submitEnabled}
              onClick={() => (submitEnabled ? handleFormSubmit() : null)}
              level="dynamicLarge"
              color={button.primaryText}
              bgColor={button.primaryBg}
              fontWeight={600}
              label={submitLabel}
            />
          </Box>
        </Box>
        <Box gap="2rem" direction="column" style={!small ? { flex: 1, minWidth: 0 } : {}}>
          {!bulkUpload && Array.isArray(optionalFormInputs) && optionalFormInputs?.length > 0 && (
            <ProductFormSectionWrapper
              small={small}
              sectionBg={page.altBg}
              sectionTitle="Optional Fields"
              titleColor={page.primaryText}
              borderColor={navigation.border}
            >
              {optionalFormInputs.map((dt) => (
                <DynamicFormRenderer
                  key={dt.key}
                  resetKey={formValues.resetKey}
                  searchTypeKey={camelSearchTypeKey}
                  fieldData={dt}
                  small={small}
                  handleFormValues={(value, key) => handleFormValues(value, key)}
                  formValues={formValues.searchFieldValues}
                  minStartDate={minStartDate}
                  timeZone={timeZone}
                  setTimeZone={setTimeZone}
                  platformOptionOverrides={platformOptionOverrides}
                />
              ))}
            </ProductFormSectionWrapper>
          )}
          {bulkUpload && (
            <ProductFormSectionWrapper
              small={small}
              sectionBg={page.altBg}
              sectionTitle="CSV Columns"
              titleColor={page.primaryText}
              borderColor={navigation.border}
            >
              <ProductFormFieldContainer
                small={small}
                primaryText={page.primaryText}
                hintText={page.hintText}
                borderColor={navigation.border}
              >
                <Box gap="0.5rem" width="100%" direction="column">
                  <Box pad={{ bottom: '0.25rem' }} border={{ side: 'bottom', color: navigation.border }}>
                    <Text size="1rem" weight={700} color={page.primaryText}>
                      Required
                    </Text>
                  </Box>
                  {requiredHeaders.map(({ name, description }) => (
                    <Box key={name} direction="row">
                      <Text size="0.9rem" color={page.primaryText} weight={600}>
                        {name}
                        {description && (
                          <>
                            :&nbsp;
                            <Text size="0.875rem" weight={400}>{description}</Text>
                          </>
                        )}
                      </Text>
                    </Box>
                  ))}
                  <Box gap="0.5rem" width="100%" direction="column" pad={{ top: '1rem' }}>
                    <Box pad={{ bottom: '0.25rem' }} border={{ side: 'bottom', color: navigation.border }}>
                      <Text size="1rem" weight={700} color={page.primaryText} pad={{ top: '1rem' }}>
                        Optional
                      </Text>
                    </Box>
                    {optionalHeaders.map(({ name, description }) => (
                      <Box key={name} direction="row">
                        <Text size="0.9rem" color={page.primaryText} weight={600}>
                          {name}
                          {description && (
                            <>
                              :&nbsp;
                              <Text size="0.875rem" weight={400}>{description}</Text>
                            </>
                          )}
                        </Text>
                      </Box>
                    ))}
                  </Box>
                  <Box pad={{ top: '0.5rem' }}>
                    <AppButton
                      overrideHover
                      onClick={() => handleDownloadExampleCSV()}
                      level="dynamicLarge"
                      width="14rem"
                      color={button.primaryText}
                      bgColor={button.primaryBg}
                      fontWeight={600}
                      label="Download CSV Template"
                    />
                  </Box>
                </Box>
              </ProductFormFieldContainer>
            </ProductFormSectionWrapper>
          )}
        </Box>
      </Box>
    </Box>
  );
};

FormContents.propTypes = {
  small: PropTypes.bool.isRequired,
  loading: PropTypes.bool.isRequired,
  underMaintenance: PropTypes.bool.isRequired,
  bulkUpload: PropTypes.bool.isRequired,
  showInfoNotice: PropTypes.bool.isRequired,
  categories: PropTypes.arrayOf(PropTypes.shape({
    category: PropTypes.string.isRequired,
    display: PropTypes.string.isRequired,
  })),
  headerMappings: PropTypes.arrayOf(PropTypes.shape({
    category: PropTypes.string.isRequired,
    headers: PropTypes.arrayOf(PropTypes.shape({
      description: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      required: PropTypes.bool.isRequired,
    })).isRequired,
  })),
  requiredFormInputs: PropTypes.arrayOf(PropTypes.any),
  optionalFormInputs: PropTypes.arrayOf(PropTypes.any),
  marketplaceFormInputs: PropTypes.arrayOf(PropTypes.any),
  showMarketplaceFields: PropTypes.bool,
  toggleShowMarketplaceFields: PropTypes.func.isRequired,
  formValues: PropTypes.shape({
    searchFieldValues: PropTypes.objectOf(PropTypes.any),
    resetKey: PropTypes.string,
  }),
  handleFormValues: PropTypes.func.isRequired,
  minStartDate: PropTypes.instanceOf(Date),
  timeZone: PropTypes.string,
  setTimeZone: PropTypes.func.isRequired,
  searchTypeKey: PropTypes.string,
  searchTypeDisplay: PropTypes.string,
  handleSearchType: PropTypes.func.isRequired,
  resetForm: PropTypes.func.isRequired,
  submitEnabled: PropTypes.bool.isRequired,
  handleFormSubmit: PropTypes.func.isRequired,
  platformOptionOverrides: PropTypes.arrayOf(PropTypes.string),
};

const ResultsUploadPage = ({
  small,
  location,
  loading,
  submitLoading,
  fetchResultsUploadConfig,
  clearResultsUploadConfig,
  submitResults,
  submitSingleResult,
  paymentPending,
  resultsFormConfig = null,
  featureConfig = null,
  resultsDisabled = false,
}) => {
  const [bulkUpload, setBulkUpload] = React.useState(true);
  const [searchType, setSearchType] = React.useState(null);
  const [searchTypeKey, setSearchTypeKey] = React.useState(null);
  const [useConfig, setUseConfig] = React.useState(null);
  const [formConfigResult, setFormConfigResult] = React.useState(null);
  const [configLoading, setConfigLoading] = React.useState(true);
  const [requiredFormInputs, setRequiredFormInputs] = React.useState(null);
  const [optionalFormInputs, setOptionalFormInputs] = React.useState(null);
  const [marketplaceFormInputs, setMarketplaceFormInputs] = React.useState(null);
  const [showMarketplaceFields, setShowMarketplaceFields] = React.useState(false);
  const [formValues, setFormValues] = React.useState({
    searchFieldValues: {}, resetKey: uniqid(),
  });
  const [selectedTimeZone, setSelectedTimeZone] = React.useState(
    Intl.DateTimeFormat().resolvedOptions().timeZone,
  );
  const { theme: { page, button }, disableResultsFeatures, isDarkMode } = useAppTheme();
  const underMaintenance = (disableResultsFeatures || resultsDisabled);

  React.useEffect(() => {
    fetchResultsUploadConfig();

    return () => clearResultsUploadConfig();
  }, []);

  React.useEffect(() => {
    if (bulkUpload && resultsFormConfig?.bulkConfig) {
      setUseConfig(resultsFormConfig.bulkConfig);
      setSearchType(resultsFormConfig.categories[0].display);
    } else if (resultsFormConfig?.singleCategoryConfig) {
      setUseConfig(resultsFormConfig.singleCategoryConfig[0].config);
      setSearchType(resultsFormConfig.categories[0].display);
    }
  }, [resultsFormConfig, bulkUpload]);

  React.useEffect(() => {
    if (useConfig) {
      setFormConfigResult(useConfig);

      const firstCategory = resultsFormConfig?.categories?.[0];

      if (bulkUpload && firstCategory) {
        setFormValues({
          searchFieldValues: { category: firstCategory.category }, resetKey: uniqid(),
        });
        setSearchTypeKey(firstCategory.category);
        setSearchType(firstCategory.display);
      } else if (firstCategory && !formValues.searchFieldValues.category) {
        setFormValues({
          searchFieldValues: { category: firstCategory.category }, resetKey: uniqid(),
        });
        setSearchTypeKey(firstCategory.category);
        setSearchType(firstCategory.display);
      }
    }
  }, [bulkUpload, useConfig]);

  React.useEffect(() => {
    if (formConfigResult) {
      const partitionedResults = _.partition(formConfigResult, 'required');
      const [requiredInputs, optionalInputs] = partitionedResults;

      const marketplaceInputs = optionalInputs?.filter(({ config }) => (
        !!config?.isMarketplaceField
      ));
      const nonMarketplaceOptionalInputs = optionalInputs?.filter(
        ({ config }) => !config?.isMarketplaceField,
      );

      setRequiredFormInputs(requiredInputs);
      setOptionalFormInputs(nonMarketplaceOptionalInputs);
      setMarketplaceFormInputs(marketplaceInputs);
      setConfigLoading(false);
    }
  }, [formConfigResult]);

  React.useEffect(() => {
    if (requiredFormInputs && optionalFormInputs) {
      const requiredKeys = requiredFormInputs.map(({ key }) => key);
      const optionalKeys = optionalFormInputs.map(({ key }) => key);
      const selectedKeys = Object.keys(formValues.searchFieldValues);
      const unsupportedKeys = selectedKeys?.filter((k) => (
        !requiredKeys.includes(k) && !optionalKeys.includes(k) && k !== 'category'
      ));

      if (unsupportedKeys?.length > 0) {
        const updatedValues = { ...formValues.searchFieldValues };

        unsupportedKeys.forEach((key) => {
          delete updatedValues[key];
        });

        setFormValues({ ...formValues, searchFieldValues: updatedValues });
      }
    }
  }, [requiredFormInputs, optionalFormInputs]);

  const handleFormValues = (value, key) => {
    const updatedValues = { ...formValues.searchFieldValues };

    if (!value || value?.length === 0) {
      delete updatedValues[key];
      setFormValues({ ...formValues, searchFieldValues: updatedValues });
      return;
    }

    updatedValues[key] = value;
    setFormValues({ ...formValues, searchFieldValues: updatedValues });
  };

  const resetForm = () => setFormValues({ searchFieldValues: {}, resetKey: uniqid() });

  const handleSearchType = (searchTypeName) => {
    const useTypeConfig = resultsFormConfig?.categories?.find(
      (cat) => cat.display === searchTypeName,
    );
    if (useTypeConfig) {
      setSearchType(searchTypeName);
      setSearchTypeKey(useTypeConfig.category);

      let newConfig = null;
      if (bulkUpload && resultsFormConfig?.bulkConfig) {
        newConfig = resultsFormConfig.bulkConfig;
      } else if (!bulkUpload && resultsFormConfig?.singleCategoryConfig) {
        const foundCategoryConfig = resultsFormConfig.singleCategoryConfig.find(
          (cat) => cat.category === useTypeConfig.category,
        );
        newConfig = foundCategoryConfig?.config || [];
      }

      setUseConfig(newConfig);
      setFormValues({
        ...formValues,
        searchFieldValues: { ...formValues.searchFieldValues, category: useTypeConfig.category },
        resetKey: uniqid(),
      });
    }
  };

  const handleBulkSelect = () => {
    resetForm();
    setBulkUpload(!bulkUpload);
  };

  const toggleShowMarketplaceFields = () => {
    setShowMarketplaceFields(!showMarketplaceFields);
  };

  const requiredKeys = requiredFormInputs?.map(({ key }) => key);
  const submitEnabled = !paymentPending && requiredKeys?.every((key) => (
    key in formValues.searchFieldValues
    && (formValues.searchFieldValues[key].length > 0
    || formValues.searchFieldValues[key] !== null)
  ));
  const platformOptionOverrides = featureConfig?.enabledSearchPlatforms;
  const hasPublisherPremium = resultsFormConfig?.categories?.some(({ category }) => category === 'publisher_premium');
  const hasPublisherPremiumSocial = resultsFormConfig?.categories?.some(({ category }) => category === 'publisher_premium_social');
  const isPublisher = searchTypeKey === 'publisher_premium' || searchTypeKey === 'publisher_premium_social';
  const showInfoNotice = Boolean(hasPublisherPremium && hasPublisherPremiumSocial && isPublisher);

  const handleFormSubmit = () => {
    if (paymentPending) return;

    if (bulkUpload) {
      /* eslint-disable camelcase */
      submitResults(
        {
          ...formValues.searchFieldValues,
          timezone: selectedTimeZone,
          via_admin: false,
          dummy_recurring: true,
        },
        () => resetForm(),
      );
    } else {
      submitSingleResult(
        {
          ...formValues.searchFieldValues,
          timezone: selectedTimeZone,
          via_admin: false,
          dummy_recurring: true,
        },
        () => resetForm(),
      );
    }
    /* eslint-enable camelcase */
  };

  const submitMessage = bulkUpload ? 'Uploading bulk results...' : 'Uploading result...';

  return (
    <AuthNavWrapper
      small={small}
      location={location}
      bannerProps={{
        title: 'Upload infringing results',
        subTitle: searchType ? `${searchType}` : null,
        bannerChildrenPosition: 'bottom',
        bannerChildren: resultsFormConfig && (
          <BannerChildren
            bgColor={page.altBg}
            textColor={page.primaryText}
            activeBg={isDarkMode ? page.highlight : 'white'}
            setBulkUpload={() => handleBulkSelect()}
          />
        ),
      }}
    >
      <Seo />
      {submitLoading && (
        <SubmissionLoader
          textColor={page.primaryText}
          loaderColor={button.highlight}
          message={submitMessage}
          contentWidth="20rem"
        />
      )}
      <FormContents
        small={small}
        key={bulkUpload ? 'bulk-results' : 'individual-results'}
        bulkUpload={bulkUpload}
        showInfoNotice={showInfoNotice}
        loading={loading || configLoading}
        underMaintenance={underMaintenance}
        requiredFormInputs={requiredFormInputs}
        optionalFormInputs={optionalFormInputs}
        marketplaceFormInputs={marketplaceFormInputs}
        showMarketplaceFields={showMarketplaceFields}
        toggleShowMarketplaceFields={toggleShowMarketplaceFields}
        formValues={formValues}
        handleFormValues={handleFormValues}
        handleSearchType={handleSearchType}
        searchTypeKey={searchTypeKey}
        searchTypeDisplay={searchType}
        categories={resultsFormConfig?.categories}
        headerMappings={resultsFormConfig?.headerMappings}
        minStartDate={null}
        timeZone={selectedTimeZone}
        setTimeZone={setSelectedTimeZone}
        resetForm={resetForm}
        submitEnabled={Boolean(submitEnabled) && !submitLoading}
        handleFormSubmit={handleFormSubmit}
        platformOptionOverrides={platformOptionOverrides}
      />
    </AuthNavWrapper>
  );
};

function mapStateToProps(state) {
  return {
    resultsFormConfig: state.productResults.resultsUploadConfig,
    loading: state.fetchLoader.dataLoading,
    submitLoading: state.fetchLoader.submissionLoading,
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators({
    fetchResultsUploadConfig: initiateResultsUploadConfigRequest,
    clearResultsUploadConfig: clearResultsUploadConfigRequest,
    submitResults: submitProductResultsImportRequest,
    submitSingleResult: submitProductSingleResultImportRequest,
  }, dispatch);
}

ResultsUploadPage.propTypes = {
  small: PropTypes.bool.isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
    search: PropTypes.string.isRequired,
    key: PropTypes.string.isRequired,
  }).isRequired,
  loading: PropTypes.bool.isRequired,
  submitLoading: PropTypes.bool.isRequired,
  fetchResultsUploadConfig: PropTypes.func.isRequired,
  clearResultsUploadConfig: PropTypes.func.isRequired,
  submitResults: PropTypes.func.isRequired,
  submitSingleResult: PropTypes.func.isRequired,
  resultsFormConfig: PropTypes.shape({
    bulkConfig: PropTypes.arrayOf(PropTypes.shape({
      key: PropTypes.string.isRequired,
      order: PropTypes.number.isRequired,
      config: PropTypes.objectOf(PropTypes.any),
      required: PropTypes.bool,
      fieldName: PropTypes.string.isRequired,
      fieldType: PropTypes.string.isRequired,
      selectable: PropTypes.bool,
      description: PropTypes.string,
    })).isRequired,
    singleCategoryConfig: PropTypes.arrayOf(PropTypes.shape({
      category: PropTypes.string.isRequired,
      config: PropTypes.arrayOf(PropTypes.shape({
        key: PropTypes.string.isRequired,
        order: PropTypes.number.isRequired,
        config: PropTypes.objectOf(PropTypes.any),
        required: PropTypes.bool,
        fieldName: PropTypes.string.isRequired,
        fieldType: PropTypes.string.isRequired,
        selectable: PropTypes.bool,
        description: PropTypes.string,
      })).isRequired,
    })).isRequired,
    categories: PropTypes.arrayOf(PropTypes.shape({
      category: PropTypes.string.isRequired,
      display: PropTypes.string.isRequired,
    })).isRequired,
    headerMappings: PropTypes.arrayOf(PropTypes.shape({
      category: PropTypes.string.isRequired,
      headers: PropTypes.arrayOf(PropTypes.shape({
        description: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        required: PropTypes.bool.isRequired,
      })).isRequired,
    })).isRequired,
  }),
  featureConfig: PropTypes.objectOf(PropTypes.any),
  resultsDisabled: PropTypes.bool,
  paymentPending: PropTypes.bool.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(withProductAuth(ResultsUploadPage));
